import React, { useEffect } from "react";
import { ArcButton, ArcStack, ArcText } from "@src/core/ui";
import { FormState, useForm } from "react-hook-form";
import { ICreateZoneStage1Form } from "./types";
import { useNavigate } from "react-router-dom";
import { setZoneMapLocationUponCreation, useCreateZoneMutation } from "@src/features/zones/redux";
import { routesConfig } from "@src/core/routes";
import { ZoneForm } from "@src/features/zones/components/zone-form";
import { useDispatch, useSelector } from "react-redux";
import { ZoneShapeType, ZoneType } from "@src/core/features";
import { selectMapLocation } from "@src/core/controllers";


export const CreateZoneStage1Page: React.FC = () => {

  const defaultValues = {
    zoneName: '',
    zoneDescription: '',
    latitude: '',
    longitude: '',
    address: '',
    zoneRadius: 0,
    zoneType: ZoneType.TEMPORARY,
  } as ICreateZoneStage1Form;

  const { control, setValue, register, getValues, formState: { errors }, handleSubmit, watch } = useForm<ICreateZoneStage1Form>({ defaultValues });

  const navigate = useNavigate();
  const [createZone, { isLoading }] = useCreateZoneMutation();

  const dispatch = useDispatch();
  const location = useSelector(selectMapLocation);
  useEffect(() => {
    dispatch(setZoneMapLocationUponCreation({
      id: watch('id') ?? 0,
      latitude: location?.latitude ?? 0,
      longitude: location?.longitude ?? 0,
      zoneName: watch('zoneName') ?? '',
      radius: watch('zoneRadius') ?? 0,
      type: watch('zoneType') ?? ZoneType.TEMPORARY,
    }));
    return () => {
      dispatch(setZoneMapLocationUponCreation(null));
    };
  }, [location, watch]);

  const onCancel =()=> { 
    navigate(-1);
    dispatch(setZoneMapLocationUponCreation(null));
  };

  const onSubmit = async (data: ICreateZoneStage1Form) => {
    try {
      const radiusMeters = data.zoneRadius;
      const payload = {
        name: data.zoneName,
        description: data.zoneDescription,
        centerLatitude: Number(data.latitude),
        centerLongitude: Number(data.longitude),
        locationName: data.address,
        radiusMeters,
        shape: ZoneShapeType.CIRCLE, 
        zoneType: data.zoneType,
        isActive: true,
        polygonCoordinates: [], 
      };

      await createZone(payload).unwrap();
      navigate(routesConfig.zones); 
      dispatch(setZoneMapLocationUponCreation(null));
    } catch (error) {
      console.error('Failed to create zone:', error);
    }
  };

  const onDraw = () => {
    dispatch(setZoneMapLocationUponCreation(null));
    navigate(`${routesConfig.createZone}?stage=2`);
  };

  return (
    <ArcStack height="100%" direction="column" justifyContent="space-between">
      <ArcStack flex={1}>
        <ArcText.h4 fontWeight={500} marginTop={2}> CREATE ZONE </ArcText.h4>

        <ZoneForm
          control={control}
          setValue={setValue}
          register={register}
          getValues={getValues}
          formState={{ errors } as FormState<ICreateZoneStage1Form>}
          watch={watch}
        />
      </ArcStack>

      <ArcStack spacing={2} direction='column' px={1}>
        <ArcStack spacing={2} direction='row'>
          <ArcButton.ghost
            fullWidth 
            size='large'
            onClick={onCancel}> 
          Cancel 
          </ArcButton.ghost>
          <ArcButton.ghost
            fullWidth 
            size='large'
            onClick={onDraw}> 
          Draw Zone
          </ArcButton.ghost>
        </ArcStack>

        <ArcButton.brand 
          fullWidth
          size='large'
          type='submit'
          isLoading={isLoading}
          onClick={handleSubmit(onSubmit)}> 
          Create
        </ArcButton.brand>    
      </ArcStack>
    </ArcStack>
  );
};
